import React, { useState, useEffect } from 'react';
import html2pdf from 'html2pdf.js';
import logo from '../../assets/logo.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const themeGray = '#f1f1f1';
const themeDark = '#6e6e6e';
const themeGreen = '#111827';
const themeBlue = '#f3f4f6';

const ImagePlaceholderSVG = () => (
    <svg viewBox="0 0 200 200" preserveAspectRatio="none" style={{ width: '100%', height: '100%', display: 'block', backgroundColor: '#d6f0ff' }}>
        {/* Cloud */}
        <circle cx="80" cy="75" r="20" fill="white" />
        <circle cx="105" cy="60" r="25" fill="white" />
        <circle cx="130" cy="75" r="20" fill="white" />
        <rect x="80" y="75" width="50" height="20" fill="white" />
        {/* Back Hill */}
        <path d="M 0 160 Q 50 130 120 160 T 200 150 L 200 200 L 0 200 Z" fill="#b8d465" />
        {/* Front Hill */}
        <path d="M -20 200 Q 50 150 130 190 T 220 170 L 220 200 Z" fill="#88af00" />
    </svg>
);

const RegistroFotografico = ({ patient, onCancel }) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('ocular_externa');
    
    // Custom Notes Modal state
    const [pendingNoteUpload, setPendingNoteUpload] = useState(null);
    const [noteText, setNoteText] = useState('');

    useEffect(() => {
        loadImages();
    }, [patient.id]);

    const loadImages = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/clinical-records/patient-images/?patient=${patient.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setImages(data.results || data);
            }
        } catch (error) {
            console.error('Error fetching images:', error);
        }
    };

    const handleUpload = async (category, subcategory, file, notes = '') => {
        setLoading(true);
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('patient', patient.id);
        formData.append('category', category);
        if (subcategory) formData.append('subcategory', subcategory);
        formData.append('image', file);
        if (notes) formData.append('notes', notes);

        try {
            const res = await fetch(`${API_URL}/api/clinical-records/patient-images/`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            if (res.ok) {
                loadImages();
            } else {
                alert('Error al subir la imagen');
            }
        } catch (error) {
            console.error('Error uploading:', error);
            alert('Error al subir la imagen');
        }
        setLoading(false);
    };

    const handleExportPDF = () => {
        const wrapper = document.getElementById('pdf-report-container-wrapper');
        const element = document.getElementById('pdf-report-container');
        if (!element || !wrapper) return;
        
        // Show the wrapper but push it off-screen so html2canvas can capture it
        wrapper.style.display = 'block';
        wrapper.style.position = 'absolute';
        wrapper.style.left = '-9999px';
        wrapper.style.top = '0';
        
        const opt = {
          margin:       10,
          filename:     `Registro_Fotografico_Optico_${patient.first_name}_${patient.last_name}.pdf`,
          image:        { type: 'jpeg', quality: 0.98 },
          html2canvas:  { scale: 2, useCORS: true },
          jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        html2pdf().set(opt).from(element).save().then(() => {
            wrapper.style.display = 'none';
        });
    };

    // Nada se borra: al reemplazar una foto se muestra la más reciente y las anteriores quedan como versiones.
    const getVersions = (category, subcategory = null) => images
        .filter(img => img.category === category && (!subcategory || img.subcategory === subcategory))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const getImage = (category, subcategory = null) => getVersions(category, subcategory)[0];

    const getImages = (category) => {
        return images.filter(img => img.category === category);
    };

    const handleFileChange = (e, category, subcategory = null, notes = '') => {
        const file = e.target.files[0];
        if (file) {
            handleUpload(category, subcategory, file, notes);
        }
    };

    const ImageBox = ({ category, subcategory, label, width = '30%' }) => {
        const versions = getVersions(category, subcategory);
        const img = versions[0];
        const anteriores = versions.slice(1);
        const inputId = `upload-${category}-${subcategory}`;

        return (
            <div style={{ width: width, textAlign: 'center', marginBottom: '20px' }}>
                <div 
                    style={{ 
                        backgroundColor: '#e0f2fe', 
                        height: '180px', 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        position: 'relative',
                        cursor: 'pointer',
                        borderRadius: '10px',
                        border: '1.5px dashed #0284c7',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.06)'
                    }}
                    onClick={() => !img && document.getElementById(inputId).click()}
                >
                    {img ? (
                        <>
                            <img src={img.image} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            <button
                                onClick={(e) => { e.stopPropagation(); document.getElementById(inputId).click(); }}
                                title="Reemplazar foto (la anterior se conserva)"
                                style={{ position: 'absolute', top: 6, right: 6, padding: '3px 9px', borderRadius: '999px', border: 'none', background: 'rgba(17,24,39,0.8)', color: '#fff', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                            >
                                <i className="bi bi-arrow-repeat me-1"></i>Reemplazar
                            </button>
                        </>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#0369a1' }}>
                            <i className="bi bi-camera-fill" style={{ fontSize: '2rem', marginBottom: '4px' }}></i>
                            <span style={{ fontSize: '11px', fontWeight: 'bold' }}>Clic para subir foto</span>
                        </div>
                    )}
                </div>
                <input 
                    type="file" 
                    id={inputId} 
                    style={{ display: 'none' }} 
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, category, subcategory)}
                />
                <h6 style={{ marginTop: '10px', color: '#374151', fontWeight: 'bold', fontSize: '13px' }}>{label}</h6>
                {anteriores.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap', fontSize: '11px', color: '#6b7280' }}>
                        <span>Versiones anteriores:</span>
                        {anteriores.map(v => (
                            <a key={v.id} href={v.image} target="_blank" rel="noopener noreferrer" title={`Subida el ${new Date(v.created_at).toLocaleString()}`}>
                                <img src={v.image} alt="Versión anterior" style={{ width: '34px', height: '34px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #d1d5db' }} />
                            </a>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const MultipleImageBox = ({ category, label, showNotes = false }) => {
        const imgs = getImages(category);
        const inputId = `upload-multiple-${category}`;

        const isPdf = (url) => url && url.toLowerCase().endsWith('.pdf');
        const isWord = (url) => url && (url.toLowerCase().endsWith('.doc') || url.toLowerCase().endsWith('.docx'));

        return (
            <div style={{ marginBottom: '30px' }}>
                <h5 style={{ color: '#1f2937', fontWeight: 'bold', fontSize: '1rem' }}>{label}</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginTop: '15px' }}>
                    {imgs.map(img => (
                        <div key={img.id} style={{ width: '30%', minWidth: '150px', textAlign: 'center', marginBottom: '15px' }}>
                            <div style={{ position: 'relative', backgroundColor: '#f3f4f6', height: '150px', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {isPdf(img.image) ? (
                                    <a href={img.image} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#dc3545' }}>
                                        <i className="bi bi-file-earmark-pdf-fill" style={{ fontSize: '3.5rem' }}></i>
                                        <div style={{ fontSize: '12px', marginTop: '4px', fontWeight: '600' }}>Ver PDF</div>
                                    </a>
                                ) : isWord(img.image) ? (
                                    <a href={img.image} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#0d6efd' }}>
                                        <i className="bi bi-file-earmark-word-fill" style={{ fontSize: '3.5rem' }}></i>
                                        <div style={{ fontSize: '12px', marginTop: '4px', fontWeight: '600' }}>Ver Documento</div>
                                    </a>
                                ) : (
                                    <img src={img.image} alt="Documento" style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }} onClick={() => window.open(img.image, '_blank')} />
                                )}
</div>
                            {showNotes && (
                                <p style={{ fontSize: '12px', color: '#4b5563', marginTop: '5px', wordWrap: 'break-word', fontWeight: '600' }}>
                                    {img.notes || 'Sin descripción'}
                                </p>
                            )}
                        </div>
                    ))}

                    <div style={{ width: '30%', minWidth: '150px', textAlign: 'center', marginBottom: '15px' }}>
                        <div 
                            style={{ 
                                backgroundColor: '#f0fdf4', 
                                border: '1.5px dashed #16a34a',
                                borderRadius: '8px',
                                height: '150px', 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                flexDirection: 'column',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                            }}
                            onClick={() => document.getElementById(inputId).click()}
                        >
                            <i className="bi bi-plus-circle-fill" style={{ fontSize: '2rem', color: '#16a34a' }}></i>
                            <span style={{ fontSize: '12px', color: '#16a34a', marginTop: '8px', fontWeight: 'bold' }}>Añadir imagen/archivo</span>
                        </div>
                        <input 
                            type="file" 
                            id={inputId} 
                            style={{ display: 'none' }} 
                            accept={['consentimiento', 'receta', 'laboratorio', 'estudios_especiales'].includes(category) ? "image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "image/*"}
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                if (showNotes) {
                                    setPendingNoteUpload({ file, category, inputTarget: e.target });
                                    setNoteText('');
                                } else {
                                    handleUpload(category, null, file, '');
                                    e.target.value = null;
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const PrintImageBox = ({ category, subcategory, label, width = '30%' }) => {
        const img = getImage(category, subcategory);
        return (
            <div style={{ width: width, textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ backgroundColor: '#f3f4f6', height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                    {img ? (
                        <img src={img.image} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        <div style={{ color: '#9ca3af', fontSize: '12px' }}>Sin imagen registrada</div>
                    )}
                </div>
                <h6 style={{ marginTop: '8px', color: '#374151', fontWeight: 'bold', fontSize: '13px' }}>{label}</h6>
            </div>
        );
    };

    const PrintMultipleImageBox = ({ category, label, showNotes = false, large = false }) => {
        const imgs = getImages(category);
        if (imgs.length === 0) return null;
        
        const boxWidth = large ? '48%' : '30%';
        const boxHeight = large ? '260px' : '180px';

        return (
            <div style={{ marginBottom: '30px' }}>
                <h5 style={{ color: '#111827', fontWeight: 'bold', fontSize: '14px', borderBottom: '1.5px solid #111827', paddingBottom: '4px' }}>{label}</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginTop: '15px' }}>
                    {imgs.map(img => (
                        <div key={img.id} style={{ width: boxWidth, minWidth: '150px', textAlign: 'center', marginBottom: '15px' }}>
                            <div style={{ backgroundColor: '#f3f4f6', height: boxHeight, overflow: 'hidden', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                                <img src={img.image} alt="Estudio óptico" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </div>
                            {showNotes && (
                                <p style={{ fontSize: '12px', color: '#4b5563', marginTop: '6px', wordWrap: 'break-word', fontWeight: 'bold' }}>
                                    {img.notes || 'Sin descripción'}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(17, 24, 39, 0.75)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
            <div style={{ width: '100%', maxWidth: '1140px', maxHeight: '92vh', backgroundColor: '#ffffff', borderRadius: '16px', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', overflow: 'hidden', position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', backgroundColor: '#111827', color: '#ffffff' }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-camera-fill text-primary" style={{ fontSize: '1.4rem' }}></i>
                        <h5 style={{ margin: 0, fontWeight: '700', color: '#ffffff' }}>Registro Fotográfico y Diagnóstico Ocular</h5>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <button onClick={handleExportPDF} className="btn btn-sm btn-primary" style={{ fontWeight: '600', borderRadius: '6px', padding: '6px 14px' }}>
                            <i className="bi bi-file-earmark-pdf-fill me-1"></i> Exportar Reporte PDF
                        </button>
                        <button type="button" onClick={onCancel} style={{ background: 'transparent', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                </div>

                <div style={{ padding: '24px 32px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' }}>
                    
                    {/* Header Paciente */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', backgroundColor: '#ffffff', padding: '16px 20px', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img src={logo} alt="Vision-Click Logo" style={{ width: '60px', height: '60px', objectFit: 'contain', marginRight: '16px' }} />
                            <div>
                                <h4 style={{ color: '#111827', fontWeight: '800', margin: 0 }}>
                                    {patient.first_name} {patient.last_name}
                                </h4>
                                <div style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '2px' }}>
                                    Cédula: <strong>{patient.cedula || 'N/A'}</strong> | HC: <strong>{patient.history_number || patient.id}</strong>
                                </div>
                            </div>
                        </div>
                        <span className="badge bg-primary" style={{ padding: '8px 12px', fontSize: '0.85rem', borderRadius: '8px' }}>
                            Óptica & Optometría
                        </span>
                    </div>

                    {/* Tabs de Navegación */}
                    <div style={{ display: 'flex', gap: '8px', borderBottom: '2px solid #e5e7eb', marginBottom: '24px', overflowX: 'auto', paddingBottom: '2px' }}>
                        {[
                            { id: 'ocular_externa', label: 'Ocular Externa / Párpados', icon: 'bi-eye' },
                            { id: 'fondo_ojo', label: 'Fondo de Ojo / Retinografía', icon: 'bi-circle' },
                            { id: 'lampara_hendidura', label: 'Lámpara de Hendidura', icon: 'bi-brightness-high' },
                            { id: 'estudios_especiales', label: 'Topografía y Campimetría', icon: 'bi-diagram-3' },
                            { id: 'consentimiento', label: 'Consentimientos Escaneados', icon: 'bi-file-earmark-check' },
                            { id: 'receta', label: 'Recetas Anteriores', icon: 'bi-receipt' },
                        ].map(tab => (
                            <button
                                key={tab.id}
                                style={{
                                    padding: '10px 16px',
                                    color: activeTab === tab.id ? '#111827' : '#6b7280',
                                    fontWeight: activeTab === tab.id ? '700' : '500',
                                    backgroundColor: activeTab === tab.id ? '#ffffff' : 'transparent',
                                    border: activeTab === tab.id ? '1px solid #e5e7eb' : 'none',
                                    borderBottom: activeTab === tab.id ? '2px solid #111827' : 'none',
                                    borderRadius: '8px 8px 0 0',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '0.875rem',
                                    whiteSpace: 'nowrap',
                                    transition: 'all 0.15s ease'
                                }}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <i className={`bi ${tab.icon}`}></i>
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </div>

                    {/* Contenido según Tab */}
                    {activeTab === 'ocular_externa' && (
                        <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '12px' }}>
                            <h5 style={{ color: '#111827', fontWeight: '700', marginBottom: '16px' }}>Fotografía Ocular Externa y Segmento Anterior</h5>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                                <ImageBox category="ocular_externa" subcategory="od" label="Ojo Derecho (OD)" />
                                <ImageBox category="ocular_externa" subcategory="oi" label="Ojo Izquierdo (OI)" />
                                <ImageBox category="ocular_externa" subcategory="ambos" label="Ambos Ojos / Párpados" />
                            </div>
                        </div>
                    )}

                    {activeTab === 'fondo_ojo' && (
                        <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '12px' }}>
                            <h5 style={{ color: '#111827', fontWeight: '700', marginBottom: '16px' }}>Fondo de Ojo y Retinografía</h5>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                                <ImageBox category="fondo_ojo" subcategory="retinografia_od" label="Retinografía OD" />
                                <ImageBox category="fondo_ojo" subcategory="retinografia_oi" label="Retinografía OI" />
                                <ImageBox category="fondo_ojo" subcategory="macula_papila" label="Nervio Óptico / Mácula" />
                            </div>
                        </div>
                    )}

                    {activeTab === 'lampara_hendidura' && (
                        <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '12px' }}>
                            <h5 style={{ color: '#111827', fontWeight: '700', marginBottom: '16px' }}>Biomicroscopía / Lámpara de Hendidura</h5>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                                <ImageBox category="lampara_hendidura" subcategory="cornea" label="Córnea y Superficie" />
                                <ImageBox category="lampara_hendidura" subcategory="cristalino" label="Cristalino / Cámara Anterior" />
                                <ImageBox category="lampara_hendidura" subcategory="iris" label="Iris y Pupila" />
                            </div>
                        </div>
                    )}

                    {activeTab === 'estudios_especiales' && (
                        <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '12px' }}>
                            <MultipleImageBox category="estudios_especiales" label="Topografía Corneal, Campimetría y Exámenes Especiales" showNotes={true} />
                        </div>
                    )}

                    {activeTab === 'consentimiento' && (
                        <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '12px' }}>
                            <MultipleImageBox category="consentimiento" label="Documentos y Consentimientos Informados Escaneados" showNotes={true} />
                        </div>
                    )}

                    {activeTab === 'receta' && (
                        <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '12px' }}>
                            <MultipleImageBox category="receta" label="Recetas Dióptricas Anteriores y Fichas Externas" showNotes={true} />
                        </div>
                    )}

                </div>
                
                {/* HIDDEN PDF CONTAINER */}
                <div style={{ display: 'none' }} id="pdf-report-container-wrapper">
                    <div id="pdf-report-container" style={{ padding: '30px', backgroundColor: '#ffffff', color: '#111827', width: '720px', fontFamily: 'Arial, sans-serif' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px', borderBottom: '2px solid #111827', paddingBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <img src={logo} alt="Vision-Click" style={{ width: '90px', height: '90px', objectFit: 'contain', marginRight: '20px' }} />
                                <div>
                                    <h2 style={{ color: '#111827', fontWeight: '900', fontSize: '24px', margin: 0 }}>INFORME FOTOGRÁFICO OCULAR</h2>
                                    <div style={{ color: '#4b5563', fontSize: '13px', marginTop: '4px' }}>Centro Óptico Especializado Vision-Click</div>
                                </div>
                            </div>
                            <div style={{ textAlign: 'right', fontSize: '12px', color: '#4b5563' }}>
                                <div>Fecha: {new Date().toLocaleDateString('es-EC')}</div>
                                <div>HC: <strong>{patient.history_number || patient.id}</strong></div>
                            </div>
                        </div>

                        <div style={{ backgroundColor: '#f9fafb', padding: '12px', borderRadius: '6px', marginBottom: '24px', fontSize: '13px' }}>
                            <strong>Paciente:</strong> {patient.first_name} {patient.last_name} | <strong>Cédula:</strong> {patient.cedula || 'N/A'} | <strong>Edad:</strong> {patient.age || 'N/A'} años
                        </div>

                        <h4 style={{ color: '#111827', fontWeight: 'bold', fontSize: '14px', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>1. FOTOGRAFÍA OCULAR EXTERNA</h4>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '15px', marginBottom: '25px', marginTop: '10px' }}>
                            <PrintImageBox category="ocular_externa" subcategory="od" label="Ojo Derecho (OD)" />
                            <PrintImageBox category="ocular_externa" subcategory="oi" label="Ojo Izquierdo (OI)" />
                            <PrintImageBox category="ocular_externa" subcategory="ambos" label="Ambos Ojos" />
                        </div>

                        <h4 style={{ color: '#111827', fontWeight: 'bold', fontSize: '14px', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>2. FONDO DE OJO / RETINOGRAFÍA</h4>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '15px', marginBottom: '25px', marginTop: '10px' }}>
                            <PrintImageBox category="fondo_ojo" subcategory="retinografia_od" label="Retinografía OD" />
                            <PrintImageBox category="fondo_ojo" subcategory="retinografia_oi" label="Retinografía OI" />
                            <PrintImageBox category="fondo_ojo" subcategory="macula_papila" label="Nervio Óptico / Mácula" />
                        </div>

                        <div className="html2pdf__page-break" style={{ pageBreakBefore: 'always' }}></div>

                        <h4 style={{ color: '#111827', fontWeight: 'bold', fontSize: '14px', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>3. BIOMICROSCOPÍA / LÁMPARA DE HENDIDURA</h4>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '15px', marginBottom: '25px', marginTop: '10px' }}>
                            <PrintImageBox category="lampara_hendidura" subcategory="cornea" label="Córnea" />
                            <PrintImageBox category="lampara_hendidura" subcategory="cristalino" label="Cristalino" />
                            <PrintImageBox category="lampara_hendidura" subcategory="iris" label="Iris" />
                        </div>

                        <PrintMultipleImageBox category="estudios_especiales" label="4. TOPOGRAFÍA CORNEAL Y CAMPIMETRÍA" showNotes={true} />
                        <PrintMultipleImageBox category="receta" label="5. RECETAS Y ANTECEDENTES" showNotes={true} />
                    </div>
                </div>
                
                {loading && (
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div className="spinner-border text-dark" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                    </div>
                )}

                {/* MODAL DE NOTAS */}
                {pendingNoteUpload && (
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ width: '420px', backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', textAlign: 'center' }}>
                            <h5 style={{ color: '#111827', fontWeight: 'bold', marginBottom: '12px' }}>Añadir Descripción / Nota Clínica</h5>
                            <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '16px' }}>Introduce una nota u observación para esta imagen (opcional):</p>
                            <textarea
                                className="form-control"
                                rows="3"
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                placeholder="Ej: Topografía corneal OD compatible con astigmatismo regular..."
                                style={{ marginBottom: '20px', resize: 'none' }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                                <button 
                                    className="btn btn-light border flex-grow-1"
                                    onClick={() => {
                                        if(pendingNoteUpload.inputTarget) pendingNoteUpload.inputTarget.value = null;
                                        setPendingNoteUpload(null);
                                    }}
                                    style={{ fontWeight: '600', borderRadius: '6px' }}
                                >
                                    Cancelar
                                </button>
                                <button 
                                    className="btn btn-primary flex-grow-1"
                                    onClick={() => {
                                        handleUpload(pendingNoteUpload.category, null, pendingNoteUpload.file, noteText);
                                        if(pendingNoteUpload.inputTarget) pendingNoteUpload.inputTarget.value = null;
                                        setPendingNoteUpload(null);
                                    }}
                                    style={{ fontWeight: '600', borderRadius: '6px' }}
                                >
                                    Guardar Imagen
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RegistroFotografico;
